import * as React from "react";
import type { ISiteScript, ISiteScriptPlacement } from "@newxsystems/types";

type ParsedSnippetTag = {
  attributes: Record<string, string | boolean>;
  content: string;
  tagName: "noscript" | "script";
};

const tagPattern = /<(script|noscript)\b([^>]*)>([\s\S]*?)<\/\1>/gi;
const attributePattern = /([:@\w.-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>`]+)))?/g;
const htmlCommentPattern = /<!--[\s\S]*?-->/g;

const attributeNameMap: Record<string, string> = {
  charset: "charSet",
  crossorigin: "crossOrigin",
  nomodule: "noModule",
  referrerpolicy: "referrerPolicy",
};

const normalizeAttributeName = (name: string) => attributeNameMap[name.toLowerCase()] ?? name;

const parseAttributes = (value: string) => {
  const attributes: Record<string, string | boolean> = {};
  let match: RegExpExecArray | null;

  attributePattern.lastIndex = 0;

  while ((match = attributePattern.exec(value))) {
    const name = normalizeAttributeName(match[1]);
    attributes[name] = match[2] ?? match[3] ?? match[4] ?? true;
  }

  return attributes;
};

const parseSnippetTags = (html: string) => {
  const tags: ParsedSnippetTag[] = [];
  let match: RegExpExecArray | null;

  tagPattern.lastIndex = 0;

  while ((match = tagPattern.exec(html))) {
    tags.push({
      attributes: parseAttributes(match[2] ?? ""),
      content: match[3] ?? "",
      tagName: match[1].toLowerCase() as ParsedSnippetTag["tagName"],
    });
  }

  return tags;
};

const renderSnippetTag = (tag: ParsedSnippetTag, key: string) => {
  if (tag.tagName === "noscript") {
    return (
      <noscript
        {...(tag.attributes as React.HTMLAttributes<HTMLElement>)}
        dangerouslySetInnerHTML={{ __html: tag.content }}
        key={key}
        suppressHydrationWarning
      />
    );
  }

  const props = tag.attributes as React.ScriptHTMLAttributes<HTMLScriptElement>;
  const content = tag.content.trim();

  return (
    <script
      {...props}
      {...(content ? { dangerouslySetInnerHTML: { __html: tag.content } } : {})}
      key={key}
      suppressHydrationWarning
    />
  );
};

const renderInlineScript = (script: ISiteScript, key: string) => {
  const html = script.html.trim();

  if (/^https?:\/\//i.test(html)) {
    return <script key={key} src={html} suppressHydrationWarning />;
  }

  return <script dangerouslySetInnerHTML={{ __html: html }} key={key} suppressHydrationWarning />;
};

const renderSiteScript = (script: ISiteScript, index: number) => {
  const tags = parseSnippetTags(script.html);
  const baseKey = `${script.id}-${index}`;

  if (tags.length === 0) {
    return [renderInlineScript(script, baseKey)];
  }

  return tags.map((tag, tagIndex) => renderSnippetTag(tag, `${baseKey}-${tagIndex}`));
};

export function SiteScripts(props: { placement: ISiteScriptPlacement; scripts?: ISiteScript[] }) {
  const scripts = (props.scripts ?? []).filter(
    (script) =>
      script.placement === props.placement && script.html.replace(htmlCommentPattern, "").trim(),
  );

  if (scripts.length === 0) {
    return null;
  }

  return <>{scripts.flatMap(renderSiteScript)}</>;
}
